import { AxiosError, AxiosResponse, isAxiosError } from 'axios';
import { BrandFilterOption } from '@cdaas/reusable-components-daas';
import {
  ApprovalChainList,
  ApprovalChainResponse,
  ApprovalChainStepApprovalRequestDecisionEnum,
  ApprovalChainTemplateResponse,
  ApprovalChainTemplateRequest,
  ApproverList,
  ApproverResponse,
  ApproverUserGroup,
  AssetEndOfLeaseRequest,
  AssetTerminationReturnFrom,
  AssetUpdateRequest,
  CreateUserRequest,
  EndofleaseidConfirmationBody,
  ImportUsersRequest,
  InvoicingAccountOptionResponse,
  InvoicingAccountOptionResponseContents,
  MyAssetResponse,
  OrganizationAssetListResponse,
  OrganizationAssetResponse,
  UpdateUserRequest,
  UserAssetListResponse,
  UserAssetResponse,
  UserGroupList,
  UserGroupRequest,
  UserGroupResponse,
  UserOrganizationListResponse,
  UserOrganizationRequest,
  UserOrganizationResponse,
  UserOrganizationWithUserGroupsResponse,
  UserWithOrganizationAndUserGroupResponse,
  UserWithUserGroupAndOrganizationList,
  VerifyPhoneNumberCodeRequest,
  VerifyPhoneNumberResponse,
  WebshopStateResponse,
  WebshopStateRequest,
} from '@cdaas/api-clients/dist/asset-user-openapi';
import { LocalBundleEnvelope, PagedItemsOverviewEnvelope } from '@cdaas/api-clients/dist/product-service-openapi';
import { DeliveryResponse, ProductOrder, ProductOrderListResponse } from '@cdaas/api-clients/dist/order-openapi';

import axiosInstance from './axiosInstance';
import {
  AuthTokenResponse,
  Credentials,
  DeliveryOptionsRequestBody,
  OrderConfirmationRequestBody,
  OrderRequestBody,
  User,
  UserGroup,
  SelectedEndOfLeaseOption,
} from '../types';
import { DEFAULT_PAGE_SIZE, FeatureFlags } from './constants';
import { getActiveOrganizationId } from './authUtils';
import { UserSearchSort, UserSearchSortEnum } from '../Pages/UsersAdmin/UsersAdminList/UsersAdminList';

// It is expected that these limit do not exceed when all items are requested in one request
const MAX_LIMIT = 32000;
const ORDER_MAX_LIMIT = 100;

export const getCredentials = async (code: string, redirectUri: string): Promise<Credentials | null> => {
  try {
    const res = await axiosInstance.post<AuthTokenResponse>('/api/auth/token', {
      code,
      redirectUri,
    });
    return res.data
      ? {
          accessToken: res.data.access_token,
          expiresIn: res.data.expires_in,
          expiryDate: new Date(Date.now() + res.data.expires_in * 1000),
        }
      : null;
  } catch (error) {
    console.error('Error while getting access token', error);
    return null;
  }
};

export const getAllApproversRequest = async (): Promise<ApproverList> => {
  try {
    const response: AxiosResponse<ApproverList> = await axiosInstance.get<ApproverList>(`/api/organization/${getActiveOrganizationId()}/approvers`);
    return response.data;
  } catch (error) {
    console.error('Error while getting all approvers', error);
    return { contents: [] };
  }
};

export const getApproverUserGroupRequest = async (userGroupId: string): Promise<ApproverUserGroup | null> => {
  try {
    const response: AxiosResponse<ApproverUserGroup> = await axiosInstance.get<ApproverUserGroup>(
      `/api/organization/${getActiveOrganizationId()}/approvers/user-group/${userGroupId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error while getting approver user group', error);
    return null;
  }
};

export const saveUserGroupApprovalChainRequest = async (approvalChain: ApproverList, userGroupId: string): Promise<ApproverResponse[]> => {
  try {
    const approvalChainRequestObject: ApprovalChainTemplateRequest = {
      approval_chain: approvalChain.contents,
    };
    const res = await axiosInstance.put<ApprovalChainTemplateResponse>(
      `/api/organization/${getActiveOrganizationId()}/user-groups/${userGroupId}/approval-chain`,
      approvalChainRequestObject,
    );
    return res.data.approval_chain;
  } catch (error) {
    console.error('Error while saving user group approval chain', error);
    return [];
  }
};

export const getProductListingAvailableBrands = async (): Promise<BrandFilterOption[]> => {
  try {
    const res = await axiosInstance.get<string[]>(`/api/organization/${getActiveOrganizationId()}/products/brands`);
    return res.data ? res.data.map<BrandFilterOption>(brand => ({ label: brand, checked: false })) : [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getPendingApprovalChains = async (organizationId: string, limit: number): Promise<ApprovalChainList | null> => {
  try {
    const res = await axiosInstance.get<ApprovalChainList>(`/api/user/me/pending-approval-chains?organization_id=${organizationId}&limit=${limit}`);
    return res.data;
  } catch (error) {
    console.error('Error while getting pending approval chains', error);
    return null;
  }
};

export const getOrdersPendingInApproval = async (): Promise<ProductOrderListResponse | null> => {
  try {
    const res = await axiosInstance.get<ProductOrderListResponse>(
      `/api/user/me/pending-approvals?organization_id=${getActiveOrganizationId()}&limit=${ORDER_MAX_LIMIT}`,
    );
    return res.data;
  } catch (error) {
    console.error('Error while getting orders pending in approval', error);
    return null;
  }
};

export const getBundleById = async (id: string): Promise<LocalBundleEnvelope | null> => {
  try {
    const res = await axiosInstance.get<LocalBundleEnvelope>(`/api/organization/${getActiveOrganizationId()}/products/bundle/${id}`);
    return res.data ?? null;
  } catch (error) {
    console.error('Error while getting bundle', error);
    return null;
  }
};

export const approveOrRejectOrders = async (
  orders: ProductOrder[],
  comment: string,
  mode: ApprovalChainStepApprovalRequestDecisionEnum,
): Promise<boolean> => {
  try {
    const body: OrderConfirmationRequestBody = {
      approval_chain_ids: orders.map(order => order.customer_approval.approvalChainId!),
      decision:
        mode === ApprovalChainStepApprovalRequestDecisionEnum.Approved
          ? ApprovalChainStepApprovalRequestDecisionEnum.Approved
          : ApprovalChainStepApprovalRequestDecisionEnum.Rejected,
      comment,
    };
    const res = await axiosInstance.post<void>(`/api/organization/${getActiveOrganizationId()}/approval/approve`, body);
    return res.status === 204;
  } catch (error) {
    console.error('Error while approving or rejecting orders', error);
    return false;
  }
};

export const getMyOrders = async (page: number = 0, limit: number = 50): Promise<ProductOrderListResponse | null> => {
  try {
    const res = await axiosInstance.get<ProductOrderListResponse>(
      `/api/user/me/orders?organization_id=${getActiveOrganizationId()}&page=${page}&limit=${limit}`,
    );
    return res.data ?? { contents: [], page_metadata: {} };
  } catch (error) {
    console.error('Error while getting my orders', error);
    return null;
  }
};

export const getMyOrderById = async (id: string): Promise<ProductOrder | null> => {
  try {
    const res = await axiosInstance.get<ProductOrder>(`/api/order/${id}`);
    return res.data ?? null;
  } catch (error) {
    console.error('Error while getting my order', error);
    return null;
  }
};

export const getDeliveryOptions = async (body: DeliveryOptionsRequestBody): Promise<DeliveryResponse | null> => {
  try {
    const res = await axiosInstance.post<DeliveryResponse>(`/api/organization/${getActiveOrganizationId()}/order/delivery_options`, body);
    return res.data ? res.data : null;
  } catch (error) {
    console.error('Error while getting delivery options', error);
    return null;
  }
};

export const createNewOrder = async (body: OrderRequestBody): Promise<ProductOrder | null> => {
  try {
    const res: AxiosResponse<ProductOrder> = await axiosInstance.post<ProductOrder>(`/api/organization/${getActiveOrganizationId()}/order/new`, body);
    return res.data ? res.data : null;
  } catch (error) {
    console.error('Error while creating new order', error);
    return null;
  }
};

export const createOrUpdateUserGroup = async (
  userGroup: UserGroupRequest,
  mode: 'create' | 'update',
  userGroupId?: string,
): Promise<UserGroup | null> => {
  try {
    let res: AxiosResponse<UserGroup>;
    const organizationId = getActiveOrganizationId();

    if (mode === 'create') {
      res = await axiosInstance.post<UserGroupRequest, AxiosResponse<UserGroup>>(`/api/organization/${organizationId}/user-groups`, userGroup);
    } else {
      res = await axiosInstance.put<UserGroupRequest, AxiosResponse<UserGroup>>(
        `/api/organization/${organizationId}/user-groups/${userGroupId}`,
        userGroup,
      );
    }

    return res.data ? res.data : null;
  } catch (error) {
    console.error(`Error while ${mode === 'create' ? 'creating' : 'updating'} user group`, error);
    return null;
  }
};

export const deleteUserGroup = async (userGroupId: string): Promise<boolean> => {
  try {
    const res: AxiosResponse = await axiosInstance.delete(`/api/organization/${getActiveOrganizationId()}/user-groups/${userGroupId}`);
    return res.status === 200;
  } catch (error) {
    console.error('Error while deleting user group', error);
    return Promise.reject(error);
  }
};

export const getAllUserGroups = async (): Promise<UserGroupResponse[] | null> => {
  try {
    const res: AxiosResponse<UserGroupList> = await axiosInstance.get<UserGroupList>(
      `/api/organization/${getActiveOrganizationId()}/user-groups?limit=${MAX_LIMIT}`,
    );
    return res.data && res.data.contents ? res.data.contents : null;
  } catch (error) {
    console.error('Error while getting all user groups', error);
    return null;
  }
};

export const getInvoicingAccounts = async (): Promise<InvoicingAccountOptionResponseContents[] | null> => {
  try {
    const res: AxiosResponse<InvoicingAccountOptionResponse> = await axiosInstance.get<InvoicingAccountOptionResponse>(
      `/api/organization/${getActiveOrganizationId()}/invoicing-accounts/all`,
    );
    return res.data && res.data.contents ? res.data.contents : null;
  } catch (error) {
    console.error('Error while getting invoicing accounts', error);
    return null;
  }
};

export const getUserInfo = async (): Promise<User | null | AxiosError> => {
  try {
    const res: AxiosResponse<User> = await axiosInstance.get('/api/user/me');
    return res?.data ? res.data : null;
  } catch (error) {
    console.error('Error while getting user', error);
    if (isAxiosError(error)) {
      return error;
    }
    return null;
  }
};

export const getAUser = async (id: string, userGroupId?: string): Promise<UserWithOrganizationAndUserGroupResponse | null> => {
  const organizationId = getActiveOrganizationId();
  const urlToUse = userGroupId
    ? `/api/organization/${organizationId}/user-groups/${userGroupId}/users/${id}`
    : `/api/organization/${organizationId}/users/${id}`;
  try {
    const res: AxiosResponse<UserWithOrganizationAndUserGroupResponse> = await axiosInstance.get(urlToUse);
    return res.data ?? null;
  } catch (error) {
    console.error('Error while getting a user', error);
    return null;
  }
};

export const getUsersPaged = async (
  search: string = '',
  sort: UserSearchSort = UserSearchSortEnum.Name,
  page: number = 0,
  limit: number = DEFAULT_PAGE_SIZE,
): Promise<UserWithUserGroupAndOrganizationList | null> => {
  try {
    const organizationId = getActiveOrganizationId();
    // Use encodeURIComponent to encode all possible special signs to URL encoded versions
    const res = await axiosInstance.get<UserWithUserGroupAndOrganizationList>(
      `/api/organization/${organizationId}/users?search=${encodeURIComponent(search)}&sort=${sort}&page=${page}&limit=${limit}`,
    );
    return res.data ? res.data : null;
  } catch (error) {
    console.error('Error while getting paged users', error);
    return null;
  }
};

export const getUsersFromOrganizationOrUserGroup = async (from: 'organization' | 'group', userGroupId?: string): Promise<User[] | null> => {
  try {
    const organizationId = getActiveOrganizationId();
    let res: AxiosResponse<User[]>;

    if (from === 'organization') {
      res = await axiosInstance.get<User[]>(`/api/organization/${organizationId}/users-all`);
    } else {
      res = await axiosInstance.get<User[]>(`/api/organization/${organizationId}/user-groups/${userGroupId}/users`);
    }

    return res.data ? res.data : null;
  } catch (error) {
    console.error(`Error while getting users from ${from}`, error);
    return null;
  }
};

export const importUsers = async (importUsersRequest: ImportUsersRequest[]): Promise<User[] | null> => {
  const res: AxiosResponse<User[]> = await axiosInstance.post<User[]>(`/api/organization/${getActiveOrganizationId()}/import-users`, {
    contents: importUsersRequest,
  });
  return res.data ? res.data : null;
};

export const createUser = async (user: CreateUserRequest, userGroupId: string): Promise<User | null | unknown> => {
  try {
    const res = await axiosInstance.post<User>(`/api/organization/${getActiveOrganizationId()}/user-groups/${userGroupId}/user`, user);
    return res.data ? res.data : null;
  } catch (error) {
    console.error('Error while creating user', error);
    return error;
  }
};

export const updateUser = async (updatedUser: UpdateUserRequest, userGroupId: string, userId: string): Promise<User | null | unknown> => {
  try {
    const res = await axiosInstance.put<UpdateUserRequest, AxiosResponse<User>>(
      `/api/organization/${getActiveOrganizationId()}/user-groups/${userGroupId}/users/${userId}`,
      updatedUser,
    );
    return res.data ? res.data : null;
  } catch (error) {
    console.error('Error while updating a user', error);
    return error;
  }
};

export const updateOrganization = async (updatedOrganization: UserOrganizationRequest): Promise<UserOrganizationResponse | null> => {
  try {
    const res: AxiosResponse<UserOrganizationResponse> = await axiosInstance.put<UserOrganizationRequest, AxiosResponse<UserOrganizationResponse>>(
      `/api/organization/${getActiveOrganizationId()}`,
      updatedOrganization,
    );
    return res.data ? res.data : null;
  } catch (error) {
    console.error('Error while updating a user', error);
    return null;
  }
};

export const moveUsersToUserGroup = async (
  body: {
    user_ids: string[];
  },
  newUserGroupId: string,
): Promise<User[] | null> => {
  try {
    const res: AxiosResponse<User[]> = await axiosInstance.post<User[]>(
      `/api/organization/${getActiveOrganizationId()}/user-groups/${newUserGroupId}/move-users`,
      body,
    );
    return res.data ? res.data : null;
  } catch (error) {
    console.error('Error while moving users to user group', error);
    return null;
  }
};

export const deleteUser = async (user: User): Promise<boolean> => {
  try {
    const res: AxiosResponse = await axiosInstance.delete<void>(
      `/api/organization/${getActiveOrganizationId()}/user-groups/${user.user_group?.id}/users/${user.id}`,
    );
    return res.status === 200;
  } catch (error) {
    console.error('Error while deleting user', error);
    return Promise.reject(error);
  }
};

export const getProducts = async (searchQuery: string): Promise<PagedItemsOverviewEnvelope | null> => {
  try {
    const res: AxiosResponse<PagedItemsOverviewEnvelope> = await axiosInstance.get<PagedItemsOverviewEnvelope>(
      `/api/organization/${getActiveOrganizationId()}/products${searchQuery}`,
    );
    return res.data ? res.data : null;
  } catch (error) {
    console.error('Error while getting products', error);
    return null;
  }
};

export const requestLogout = async (): Promise<void> => {
  try {
    await axiosInstance.post('/api/auth/logout');
  } catch (error) {
    console.error('Error while logging out', error);
  }
};

export const startPhoneNumberVerification = async (): Promise<VerifyPhoneNumberResponse | null> => {
  try {
    const res: AxiosResponse<VerifyPhoneNumberResponse> = await axiosInstance.post<VerifyPhoneNumberResponse>('/api/user/me/verify-phone-number');
    return res.data ? res.data : null;
  } catch (error) {
    console.error('Error while starting phone number verification', error);
    return null;
  }
};

export const verifyPhoneNumberPasscode = async (passcode: string, verificationId: string): Promise<User | null> => {
  try {
    const body: VerifyPhoneNumberCodeRequest = { code: passcode };
    const res: AxiosResponse<User> = await axiosInstance.post<User>(`/api/user/me/verify-phone-number/${verificationId}`, body);
    return res.status === 200 ? res.data : null;
  } catch (error) {
    console.error('Error while verifying phone number passcode', error);
    return null;
  }
};

export const getMyAllAssets = async (): Promise<UserAssetListResponse | null> => {
  try {
    const res: AxiosResponse<UserAssetListResponse> = await axiosInstance.get<UserAssetListResponse>(`/api/user/me/assets?limit=${MAX_LIMIT}`);
    return res.data ?? null;
  } catch (error) {
    console.error('Error while getting my assets', error);
    return null;
  }
};

export const getMyAsset = async (assetId: string): Promise<MyAssetResponse | null> => {
  try {
    const res: AxiosResponse<MyAssetResponse> = await axiosInstance.get<MyAssetResponse>(`/api/user/me/assets/${assetId}`);
    return res.data ?? null;
  } catch (error) {
    console.error('Error while getting my asset', error);
    return null;
  }
};

export interface GetOrganizationAssetsSearchParams {
  name?: string;
  phoneNumber?: string;
  email?: string;
  imei?: string;
  locationReference?: string;
  orderId?: string;
  state?: string;
  waitingForReturn: boolean;
  withEndOfLeaseOptions: boolean;
}

export const getOrganizationAssets = async (
  limit: number = 0,
  page: number = 0,
  search: GetOrganizationAssetsSearchParams,
): Promise<OrganizationAssetListResponse | null> => {
  try {
    const path =
      `/api/organization/${getActiveOrganizationId()}/assets?limit=${limit}&page=${page}` +
      `${search?.name ? `&name=${encodeURIComponent(search.name)}` : ''}` +
      `${search?.phoneNumber ? `&phone_number=${encodeURIComponent(search.phoneNumber)}` : ''}` +
      `${search?.email ? `&email=${encodeURIComponent(search.email)}` : ''}` +
      `${search?.imei ? `&imei=${encodeURIComponent(search.imei)}` : ''}` +
      `${search?.locationReference ? `&location_reference=${encodeURIComponent(search.locationReference)}` : ''}` +
      `${search?.orderId ? `&order_id=${encodeURIComponent(search.orderId)}` : ''}` +
      `${search?.state ? `&state=${search.state}` : ''}` +
      `&waiting_for_return=${search.waitingForReturn}` +
      `&with_end_of_lease_options=${search.withEndOfLeaseOptions}`;
    const res: AxiosResponse<OrganizationAssetListResponse> = await axiosInstance.get<OrganizationAssetListResponse>(path);
    return res.data ?? null;
  } catch (error) {
    console.error('Error while getting organization assets', error);
    return null;
  }
};

export const getOrganization = async (
  include?: 'user_groups' | 'user_groups,user_groups.users',
): Promise<UserOrganizationWithUserGroupsResponse | null> => {
  const organizationId = getActiveOrganizationId();
  try {
    const res: AxiosResponse<UserOrganizationWithUserGroupsResponse> = await axiosInstance.get<UserOrganizationWithUserGroupsResponse>(
      `/api/organization/${organizationId}${include ? `?include=${include}` : ''}`,
    );
    return res.data ?? null;
  } catch (error) {
    console.error('Error while getting organization', error);
    return null;
  }
};

export const getUsersAllAssets = async (userId: string): Promise<UserAssetResponse[] | null> => {
  try {
    const res: AxiosResponse<UserAssetListResponse> = await axiosInstance.get<UserAssetListResponse>(
      `/api/organization/${getActiveOrganizationId()}/assets/user/${userId}?limit=${MAX_LIMIT}`,
    );
    return res.data?.contents ?? null;
  } catch (error) {
    console.error('Error while getting users all assets', error);
    return null;
  }
};

export const getAsset = async (assetId: string): Promise<OrganizationAssetResponse | null> => {
  try {
    const res: AxiosResponse<OrganizationAssetResponse> = await axiosInstance.get<OrganizationAssetResponse>(
      `/api/organization/${getActiveOrganizationId()}/assets/${assetId}`,
    );
    return res.data ?? null;
  } catch (error) {
    console.error('Error while getting organization assets', error);
    return null;
  }
};

export const postEndOfLeaseDecision = async (
  assetId: string,
  makeDecisionAs: 'user' | 'admin',
  chosenDecision: SelectedEndOfLeaseOption,
  from?: AssetTerminationReturnFrom,
  chosenExtensionValue?: number,
  confirm = true,
): Promise<MyAssetResponse | OrganizationAssetResponse | null> => {
  try {
    let body: AssetEndOfLeaseRequest;
    if (chosenDecision === 'extension') {
      body = { extension: { length: chosenExtensionValue! } };
    } else if (chosenDecision === 'buyout') {
      body = { buyout: {}, confirm };
    } else if (chosenDecision === 'return' && from) {
      body = {
        return: {
          from,
        },
      };
    }

    const urlToUse =
      makeDecisionAs === 'user'
        ? `/api/user/me/assets/${assetId}/end-of-lease-choice`
        : `/api/organization/${getActiveOrganizationId()}/assets/${assetId}/end-of-lease-choice`;
    const res: AxiosResponse<MyAssetResponse | OrganizationAssetResponse> = await axiosInstance.post<MyAssetResponse | OrganizationAssetResponse>(
      urlToUse,
      body!,
    );
    return res.data ?? null;
  } catch (error) {
    console.error('Error while posting asset end of lease decision', error);
    return null;
  }
};

export const confirmOrCancelEndOfLeaseDecision = async (
  assetId: string,
  endOfLeaseId: string,
  confirm: boolean,
  makeDecisionAs: 'user' | 'admin',
): Promise<MyAssetResponse | OrganizationAssetResponse | null> => {
  try {
    const urlToUse =
      makeDecisionAs === 'user'
        ? `/api/user/me/assets/${assetId}/end-of-lease/${endOfLeaseId}/confirmation`
        : `/api/organization/${getActiveOrganizationId()}/assets/${assetId}/end-of-lease/${endOfLeaseId}/confirmation`;
    const body: EndofleaseidConfirmationBody = {
      confirm,
    };
    const res: AxiosResponse<MyAssetResponse | OrganizationAssetResponse> = await axiosInstance.post<MyAssetResponse | OrganizationAssetResponse>(
      urlToUse,
      body,
    );
    return res.data ?? null;
  } catch (error) {
    console.error('Error while confirming asset end of lease decision', error);
    return null;
  }
};

export const getAllAssetsAndOngoingOrdersForAUser = async (
  userId: string,
): Promise<{
  assets: OrganizationAssetListResponse | null;
  orders: ProductOrderListResponse | null;
}> => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  const [assets, orders] = await Promise.all([
    axiosInstance
      .get<OrganizationAssetListResponse>(`/api/organization/${getActiveOrganizationId()}/assets/user/${userId}?limit=${MAX_LIMIT}`)
      .then(res => Promise.resolve(res.data))
      .catch(error => {
        console.error('Error while getting assets of the user', error);
        return Promise.resolve(null);
      }),
    axiosInstance
      .get<ProductOrderListResponse>(
        `/api/order/enduser/${userId}?limit=${ORDER_MAX_LIMIT}&completed=false&created_after=${encodeURIComponent(date.toJSON())}`,
      )
      .then(res => Promise.resolve(res.data))
      .catch(error => {
        console.error('Error while getting orders of the user', error);
        return Promise.resolve(null);
      }),
  ]);
  return { assets, orders };
};

export const getApprovalChainOfOrder = async (approvalChainId: string): Promise<ApprovalChainResponse | null> => {
  try {
    const response = await axiosInstance.get<ApprovalChainResponse>(
      `/api/organization/${getActiveOrganizationId()}/approval-chains/${approvalChainId}`,
    );
    return response.data ?? null;
  } catch (error) {
    console.error('Error while getting approval chain', error);
    return null;
  }
};

export const assignAssetToOrganization = async (assetId: string, invoicingAccountId?: string): Promise<OrganizationAssetResponse | null> => {
  try {
    const response = await axiosInstance.post<OrganizationAssetResponse>(
      `/api/organization/${getActiveOrganizationId()}/assets/${assetId}/assign-user`,
      { user_id: null, invoicing_account: invoicingAccountId },
    );
    return response.data ?? null;
  } catch (error) {
    console.error('Error while assigning asset to organization', error);
    return null;
  }
};

export const assignAssetToUser = async (
  assetId: string,
  targetUserId: string,
  invoicingAccountId?: string,
): Promise<OrganizationAssetResponse | null> => {
  try {
    const response = await axiosInstance.post<OrganizationAssetResponse>(
      `/api/organization/${getActiveOrganizationId()}/assets/${assetId}/assign-user`,
      { user_id: targetUserId, invoicing_account: invoicingAccountId },
    );
    return response.data ?? null;
  } catch (error) {
    console.error('Error while assigning asset to user', error);
    return null;
  }
};

export const getOrganizationAssetReport = async (): Promise<string | null> => {
  try {
    const response = await axiosInstance.get<string>(`/api/organization/${getActiveOrganizationId()}/assets/report`);
    return response.data ?? null;
  } catch (error) {
    console.error('Error while getting organization asset report', error);
    return null;
  }
};

export const getOrganizationOrdersReport = async (): Promise<string | null> => {
  try {
    const response = await axiosInstance.get<string>(`/api/organization/${getActiveOrganizationId()}/reports/orders`);
    return response.data ?? null;
  } catch (error) {
    console.error('Error while getting organization orders report', error);
    return null;
  }
};

export const getFeatureFlags = async (): Promise<{ featureFlags: FeatureFlags } | null> => {
  try {
    const res: AxiosResponse = await axiosInstance.get('/api/feature-flags');
    return res.data ?? null;
  } catch (error) {
    console.error('Error while getting feature flags', error);
    return null;
  }
};

export const getOrganizationHierarchyForAdmin = async (): Promise<UserOrganizationResponse[] | null> => {
  try {
    const res: AxiosResponse<UserOrganizationListResponse> = await axiosInstance.get('/api/user/me/organizations');
    return res.data.contents ?? null;
  } catch (error) {
    console.error('Error while getting admin user organizations hierarchy', error);
    return null;
  }
};

export const userAcceptCompanyDevicePolicy = async (): Promise<User | null> => {
  try {
    const res: AxiosResponse<User> = await axiosInstance.post('/api/user/me/accept-company-device-policy');
    return res.data ?? null;
  } catch (error) {
    console.error('Error while accepting company device policy', error);
    return null;
  }
};

export const updateOrganizationAsset = async (assetId: string, assetUpdateRequest: AssetUpdateRequest): Promise<OrganizationAssetResponse | null> => {
  try {
    const res: AxiosResponse<OrganizationAssetResponse> = await axiosInstance.put(
      `/api/organization/${getActiveOrganizationId()}/assets/${assetId}`,
      assetUpdateRequest,
    );
    return res.data ?? null;
  } catch (error) {
    console.error('Error while updating organization asset', error);
    return null;
  }
};

/**
 * Initialize shop state to backend prior to redirecting user to external webshop.
 * State ID is received in the response and should be transmitted via query parameter when redirecting user to the webshop.
 *
 * @param orderToUser User for whom the order is created for. Can be either the active user, another user if ordering on behalf, or null if the order is for organization.
 * @returns Response object or null if error occurred
 */
export const initializeWebshopState = async (orderToUser: User | null): Promise<WebshopStateResponse | null> => {
  try {
    const request: WebshopStateRequest = { created_for_user_id: orderToUser?.id ?? null };
    const res: AxiosResponse<WebshopStateResponse> = await axiosInstance.post(
      `/api/organization/${getActiveOrganizationId()}/webshop-states`,
      request,
    );
    return res.data ?? null;
  } catch (error) {
    console.error('Error while initializing webshop state', error);
    return null;
  }
};

export const getWebshopState = async (stateId: string): Promise<WebshopStateResponse | null> => {
  try {
    const res: AxiosResponse<WebshopStateResponse> = await axiosInstance.get(
      `/api/organization/${getActiveOrganizationId()}/webshop-states/${stateId}`,
    );
    return res.data ?? null;
  } catch (error) {
    console.error('Error while getting webshop state', error);
    return null;
  }
};
